import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHeart} from "@fortawesome/free-solid-svg-icons";
import {message, Modal} from "antd";

import Loader2 from "../../Components/Loader2";
import ProductsFilter from "../Products/ProductsFilter";
import CardSVG from "../../assets/cart-plus-svgrepo-com.svg";
import {language} from "../../language/Language";
import {useGetCategorieByIdPagedQuery} from "../../RTK/Slices/Slices/apiSliceMeat";
import {getFavorites, addToFavorites, isFavorite, addToCart, calculateDiscountedPrice} from "../../utils/localStorage";

const INITIAL_FILTERS = {
    searchQuery: "",
    priceRange: [0, 6000],
    selectedCategories: [],
    sortOption: "default",
};

const API_PER_PAGE = 50;

const SORT_OPTIONS = {
    PRICE_ASC: "price_asc",
    PRICE_DESC: "price_desc",
    DISCOUNT_ASC: "discount_asc",
    DISCOUNT_DESC: "discount_desc",
    NAME_ASC: "name_asc",
    NAME_DESC: "name_desc",
    DEFAULT: "default",
};

// ======================================================
// مكون داخلي يجيب صفحة واحدة ويمرر البيانات للأب
// ======================================================
const PageFetcher = React.memo(({ id, page, onData, onDone }) => {
    const { data, isSuccess, isError } = useGetCategorieByIdPagedQuery(
        { id, page, per_page: API_PER_PAGE },
        { skip: !id }
    );

    useEffect(() => {
        if (isSuccess && data) {
            const products = data?.data?.[0]?.products?.data ?? [];
            const lastPage = data?.data?.[0]?.products?.last_page ?? 1;
            onData(page, products, lastPage);
        }
        if (isError) {
            onDone();
        }
    }, [isSuccess, isError, data, page, onData, onDone]);

    return null;
});
PageFetcher.displayName = "PageFetcher";

// ======================================================
// الـ Component الرئيسي
// ======================================================
function ProductsCategorie() {
    const {id} = useParams();
    const navigate = useNavigate();

    // كل المنتجات المجموعة من كل الصفحات
    const [allFetchedProducts, setAllFetchedProducts] = useState([]);
    const [lastPage, setLastPage] = useState(1);
    const [fetchedPages, setFetchedPages] = useState(new Set());
    const [isFetchingAll, setIsFetchingAll] = useState(true);
    const receivedRef = useRef({});
    const isFirstMount = useRef(true);

    const handlePageData = useCallback((page, products, detectedLastPage) => {
        if (receivedRef.current[page]) return;
        receivedRef.current[page] = true;

        setLastPage(detectedLastPage);
        setFetchedPages(prev => {
            const next = new Set(prev);
            next.add(page);
            return next;
        });
        setAllFetchedProducts(prev => {
            const withoutThisPage = prev.filter(p => p._fetchPage !== page);
            const tagged = products.map(p => ({ ...p, _fetchPage: page }));
            return [...withoutThisPage, ...tagged];
        });
    }, []);

    const handleFetchDone = useCallback(() => {
        setIsFetchingAll(false);
    }, []);

    useEffect(() => {
        if (lastPage > 0 && fetchedPages.size >= lastPage) {
            setIsFetchingAll(false);
        }
    }, [fetchedPages, lastPage]);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setAllFetchedProducts([]);
        setLastPage(1);
        setFetchedPages(new Set());
        setIsFetchingAll(true);
        receivedRef.current = {};
    }, [id]);

    // State للفلتر والـ UI
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [, setLocalFavorites] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // إزالة الـ _fetchPage tag من المنتجات
    const cleanProducts = useMemo(() => {
        return allFetchedProducts.map(({ _fetchPage, ...rest }) => rest);
    }, [allFetchedProducts]);

    const availableProducts = useMemo(() => {
        return cleanProducts.filter((product) => parseFloat(product.stock) > 0);
    }, [cleanProducts]);

    const maxPrice = useMemo(() => {
        if (availableProducts.length === 0) return 6000;
        return Math.max(...availableProducts.map((p) => parseFloat(p.price)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableProducts]);

    const isUserLoggedIn = useCallback(() => !!localStorage.getItem("user"), []);
    const updateLocalStorage = useCallback(() => setLocalFavorites(getFavorites()), []);
    const showLoginModal = useCallback(() => setIsModalVisible(true), []);
    const hideLoginModal = useCallback(() => setIsModalVisible(false), []);

    const sortProducts = useCallback((products, sortOption) => {
        if (sortOption === SORT_OPTIONS.DEFAULT) return products;
        const sorted = [...products];
        switch (sortOption) {
            case SORT_OPTIONS.PRICE_ASC: return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
            case SORT_OPTIONS.PRICE_DESC: return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
            case SORT_OPTIONS.DISCOUNT_ASC: return sorted.sort((a, b) => parseFloat(a.default_sales_discount) - parseFloat(b.default_sales_discount));
            case SORT_OPTIONS.DISCOUNT_DESC: return sorted.sort((a, b) => parseFloat(b.default_sales_discount) - parseFloat(a.default_sales_discount));
            case SORT_OPTIONS.NAME_ASC: return sorted.sort((a, b) => a.name.localeCompare(b.name));
            case SORT_OPTIONS.NAME_DESC: return sorted.sort((a, b) => b.name.localeCompare(a.name));
            default: return sorted;
        }
    }, []);

    const applyFilters = useCallback((products, filterOptions) => {
        let result = [...products];
        if (filterOptions.searchQuery) {
            const query = filterOptions.searchQuery.toLowerCase().trim();
            result = result.filter((p) => p.name.toLowerCase().includes(query));
        }
        result = result.filter((p) => {
            const price = parseFloat(p.price);
            return price >= filterOptions.priceRange[0] && price <= filterOptions.priceRange[1];
        });
        if (filterOptions.selectedCategories.length > 0) {
            result = result.filter((p) => filterOptions.selectedCategories.includes(p.cat_id));
        }
        return sortProducts(result, filterOptions.sortOption);
    }, [sortProducts]);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setFilteredProducts(applyFilters(availableProducts, newFilters));
    }, [availableProducts, applyFilters]);

    const handleAddToFavourite = useCallback(async (product, event) => {
        event.stopPropagation();
        if (!isUserLoggedIn()) { showLoginModal(); return; }
        const productData = {
            product_id: product.product_id, name: product.name, price: product.price,
            default_sales_discount: product.default_sales_discount || 0,
            image_url: product.image_url, stock: product.stock,
        };
        const wasAdded = addToFavorites(productData);
        message.success(wasAdded ? "تمت إضافة المنتج إلى المفضلة بنجاح!" : "تمت إزالة المنتج من المفضلة!");
        updateLocalStorage();
    }, [isUserLoggedIn, showLoginModal, updateLocalStorage]);

    const handleAddToCart = useCallback(async (product, qty, event) => {
        event.stopPropagation();
        const productData = {
            product_id: product.product_id, name: product.name, price: product.price,
            default_sales_discount: product.default_sales_discount || 0,
            image_url: product.image_url, stock: product.stock,
        };
        const success = addToCart(productData, qty);
        if (success) { message.success(`تمت إضافة ${qty} ${qty > 1 ? "قطع" : "قطعة"} إلى السلة بنجاح!`); updateLocalStorage(); }
        else message.error("فشل إضافة المنتج إلى السلة. تحقق من المخزون المتاح.");
    }, [updateLocalStorage]);

    useEffect(() => {
        updateLocalStorage();
        window.addEventListener("storage", updateLocalStorage);
        return () => window.removeEventListener("storage", updateLocalStorage);
    }, [updateLocalStorage]);

    useEffect(() => {
        if (availableProducts.length > 0) {
            const filtered = applyFilters(availableProducts, filters);
            setFilteredProducts(filtered);
            if (filters.priceRange[1] === 6000 || filters.priceRange[1] < maxPrice) {
                setFilters(prev => ({ ...prev, priceRange: [0, maxPrice] }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableProducts]);

    return (
        <div className="bg-gray-100 py-28">
            {/* جيب كل صفحات الـ API في الخلفية */}
            {Array.from({length: lastPage}, (_, i) => i + 1).map(page => (
                <PageFetcher
                    key={`${id}-page-${page}`}
                    id={id}
                    page={page}
                    onData={handlePageData}
                    onDone={handleFetchDone}
                />
            ))}

            <div className="container">
                <div className="grid grid-cols-1 gap-5">
                    <div>
                        <ProductsFilter products={cleanProducts} onFilterChange={handleFilterChange} />
                    </div>
                    <div>
                        {isFetchingAll && availableProducts.length === 0 ? (
                            <div className="flex justify-center items-center py-20">
                                <Loader2 />
                            </div>
                        ) : filteredProducts.length > 0 ? (
                            <>
                                {/* عداد المنتجات */}
                                <div className="flex items-center gap-3 mb-4">
                                    <p className="text-sm text-gray-500">
                                        إجمالي المنتجات: <strong>{filteredProducts.length}</strong>
                                    </p>
                                    {isFetchingAll && (
                                        <span className="text-xs text-blue-500 animate-pulse">
                                            جاري تحميل المزيد...
                                        </span>
                                    )}
                                </div>

                                {/* عرض كل المنتجات دفعة واحدة - infinite scroll */}
                                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {filteredProducts.map((product) => (
                                        <ProductCard
                                            key={product.product_id}
                                            product={product}
                                            onAddToFavourite={handleAddToFavourite}
                                            onAddToCart={handleAddToCart}
                                        />
                                    ))}
                                </div>

                                {/* مؤشر تحميل في الأسفل أثناء جلب الصفحات الخلفية */}
                                {isFetchingAll && (
                                    <div className="flex justify-center items-center py-8">
                                        <Loader2 />
                                    </div>
                                )}
                            </>
                        ) : !isFetchingAll ? (
                            <EmptyState />
                        ) : (
                            <div className="flex justify-center items-center py-20">
                                <Loader2 />
                            </div>
                        )}
                    </div>
                </div>
                <LoginModal
                    isVisible={isModalVisible}
                    onCancel={hideLoginModal}
                    onLogin={() => navigate("/Login")}
                />
            </div>
        </div>
    );
}

// ======================================================
// Product Card — مع عداد الكمية +/-
// ======================================================
const ProductCard = React.memo(({product, onAddToFavourite, onAddToCart}) => {
    const [qty, setQty] = useState(1);

    const discountedPrice = useMemo(() =>
        calculateDiscountedPrice(product.price, product.default_sales_discount),
        [product.price, product.default_sales_discount]
    );
    const originalPrice = useMemo(() => parseInt(product.price).toFixed(), [product.price]);
    const finalPrice = useMemo(() => parseFloat(discountedPrice).toFixed(0), [discountedPrice]);
    const hasDiscount = originalPrice !== finalPrice;

    const maxQty = Math.min(parseFloat(product.stock) || 99, 99);

    const decrease = (e) => {
        e.stopPropagation();
        setQty(prev => Math.max(1, prev - 1));
    };

    const increase = (e) => {
        e.stopPropagation();
        setQty(prev => Math.min(maxQty, prev + 1));
    };

    return (
        <div className="bg-white rounded-2xl p-5 cursor-pointer hover:-translate-y-2 transition-all relative flex flex-col">
            {product.percent && (
                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {product.percent}
                </div>
            )}
            <div className="bg-gray-100 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer absolute top-4 right-4">
                <FontAwesomeIcon
                    className={`${isFavorite(product.product_id) ? "text-HovMainColor" : "text-slate-500 hover:text-green-400"} transition-colors`}
                    onClick={(e) => onAddToFavourite(product, e)}
                    icon={faHeart}
                />
            </div>
            <Link to={`/Products/${product.product_id}`} className="flex-1">
                <div className="w-5/6 h-[180px] flex justify-center items-center overflow-hidden mx-auto mb-3">
                    <img src={product.image_url} alt={product.name} className="!h-auto !w-full object-contain" loading="lazy" />
                </div>
                <div>
                    <h3 className="text-sm font-extrabold text-center text-gray-800 line-clamp-2 min-h-[40px]">{product.name}</h3>
                    <div className="flex items-center justify-center gap-3 my-2">
                        {hasDiscount && (
                            <h4 className="text-base text-gray-400 font-bold line-through">EGP {originalPrice}</h4>
                        )}
                        <h4 className="text-lg text-gray-600 font-bold">EGP {finalPrice}</h4>
                    </div>
                </div>
            </Link>

            {/* عداد الكمية */}
            <div
                className="flex items-center justify-center gap-3 mb-3"
                onClick={(e) => e.stopPropagation()}
            >
                {/* زر ناقص */}
                <button
                    onClick={decrease}
                    className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-mainColor text-mainColor font-bold text-lg leading-none hover:bg-mainColor hover:text-white transition-colors select-none"
                    disabled={qty <= 1}
                    style={{opacity: qty <= 1 ? 0.4 : 1}}
                >
                    −
                </button>

                {/* العدد */}
                <span className="w-8 text-center font-bold text-gray-800 text-base select-none">
                    {qty}
                </span>

                {/* زر زائد */}
                <button
                    onClick={increase}
                    className="w-8 h-8 flex items-center justify-center rounded-full border-2 border-mainColor text-mainColor font-bold text-lg leading-none hover:bg-mainColor hover:text-white transition-colors select-none"
                    disabled={qty >= maxQty}
                    style={{opacity: qty >= maxQty ? 0.4 : 1}}
                >
                    +
                </button>
            </div>

            {/* زر أضف إلى السلة */}
            <div className="flex justify-center items-center">
                <button
                    onClick={(e) => onAddToCart(product, qty, e)}
                    className="relative flex items-center justify-center w-full h-[40px] bg-mainColor rounded-lg border-none shadow-md cursor-pointer overflow-hidden transition duration-500 active:scale-95 group"
                >
                    <span className="absolute left-2 flex items-center justify-center w-[28px] h-[28px] z-10 transition duration-500 group-hover:translate-x-1">
                        <img src={CardSVG} alt="Add to cart" className="w-5 h-5" />
                    </span>
                    <p className="flex items-center justify-center h-full text-sm font-semibold text-white z-10 pr-2">
                        {language === "ar" ? "أضف إلى السلة" : "Add to Cart"}
                    </p>
                </button>
            </div>
        </div>
    );
});

const EmptyState = React.memo(() => (
    <div className="bg-white p-8 rounded-lg text-center">
        <h3 className="text-xl font-medium text-gray-700">
            {language === "ar" ? "لا توجد منتجات متطابقة" : "No matching products found"}
        </h3>
        <p className="text-gray-500 mt-2">
            {language === "ar" ? "حاول تغيير معايير البحث الخاصة بك" : "Try changing your filter criteria"}
        </p>
    </div>
));

const LoginModal = React.memo(({isVisible, onCancel, onLogin}) => (
    <Modal
        title={language === "ar" ? "تنبيه" : "Warning"}
        open={isVisible}
        onCancel={onCancel}
        footer={[
            <div key="footer" className="flex justify-end items-center gap-2">
                <button className="bg-mainColor text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors" onClick={onLogin}>
                    {language === "ar" ? "تسجيل الدخول" : "Log in"}
                </button>
                <button className="bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400 transition-colors" onClick={onCancel}>
                    {language === "ar" ? "إلغاء" : "Cancel"}
                </button>
            </div>
        ]}
    >
        <p className="text-center">
            {language === "ar" ? "أنت غير مسجل. يرجى إنشاء حساب أولاً." : "You are not registered. Please create an account first."}
        </p>
    </Modal>
));

ProductCard.displayName = "ProductCard";
EmptyState.displayName = "EmptyState";
LoginModal.displayName = "LoginModal";

export default ProductsCategorie;
